import * as React from 'react';
import { perfMark } from '@atlassian/help-center-common-util/analytics/browser-metrics-v3';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';

export interface InjectedProps {
	updateRendered: (componentName: string) => void;
}
const context = React.createContext<InjectedProps>({ updateRendered: () => null });

interface AdditionalAttributes {
	[name: string]: boolean;
}

export interface ProviderProps {
	requiredComponents: string[];
	additionalAttributes: AdditionalAttributes;
	children: React.ReactNode;
}

interface RenderedComponentState {
	[name: string]: boolean;
}

const allComponentRendered = (renderedList: RenderedComponentState): boolean => {
	return Object.values(renderedList).every(Boolean);
};

const getInitialState = (requiredComponent: string[]): RenderedComponentState => {
	return requiredComponent.reduce((acc: RenderedComponentState, componentKey) => {
		acc[componentKey] = false;
		return acc;
	}, {});
};

export const BrowserMetricProvider = ({
	requiredComponents,
	additionalAttributes,
	children,
}: ProviderProps) => {
	const [renderedComponents, setRenderedComponents] = React.useState<RenderedComponentState>(
		getInitialState(requiredComponents),
	);

	const updateRendered = React.useCallback(
		(componentName: string) => {
			if (!renderedComponents[componentName]) {
				setRenderedComponents((state) => ({ ...state, [componentName]: true }));
			}
		},
		[renderedComponents],
	);

	const content = allComponentRendered(renderedComponents) ? (
		<FireScreenEventOnMount customData={additionalAttributes} />
	) : null;

	return (
		<context.Provider value={{ updateRendered }}>
			{content}
			{children}
		</context.Provider>
	);
};

interface BrowserMetricSubmitterProps {
	componentName: string;
}

export const BrowserMetricSubmitter = ({ componentName }: BrowserMetricSubmitterProps) => {
	const { updateRendered } = React.useContext(context);
	React.useEffect(() => {
		perfMark(`rendered.${componentName}`);
		updateRendered(componentName);
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

export const withBrowserMetricSubmitter =
	<TProps extends object>(componentName: string, WrappedComponent: React.ComponentType<TProps>) =>
	// upgrading eslint-plugin-react to 7.31.11. Please correct when this code is revisited.
	// eslint-disable-next-line react/display-name
	(props: TProps) => {
		return (
			<>
				<BrowserMetricSubmitter componentName={componentName} />
				<WrappedComponent {...props} />
			</>
		);
	};
